import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useCartLineItemForPet, useSessionCustomer } from '../../customer';
import {
  LoadingView,
  ServerSideErrorModal,
  NoRecipesFound,
} from '../../components';
import { CMSBlok, interpolate } from '../../cms';
import { useOffer } from '../../offer';
import { useProduct, useProductRecommendations } from '../../products';
import { usePet, usePets } from '../../pets';
import { PrefixedReporterProvider, useReporter } from '../../reporter';
import { EmptyHeader } from '../../molecules';
import { isServerError } from '../../api';
import { PlanLoader } from '../PlanLoader';
import { useLocalStorage } from '../../hooks';
import { Header } from './Header';
import { usePlanSelectionMachine } from './usePlanSelectionMachine';
import {
  getCartProduct,
  getMealPrepTypeFromRecipes,
  showNoRecipeFound,
} from './functions';
import { components } from './components';
import { STATE, ACTIONS, STATE_TO_STEP_REFERENCE } from './constants';
import {
  InterstitialContainer,
  LoadingInterstitial,
  Wrapper,
} from './styles.js';

const PLAN_SELECTION_KEY = 'PLAN_SELECTION';

export const PlanSelectionContainer = ({
  petId,
  rootPath,
  body,
  skipInterstitial,
  planLoaderMessages,
  planLoaderButtonText,
  planSelectionInfo,
  returnToQuiz,
  noRecipesInfo,
  onClickDeletePet,
  finishPlanSelection,
}) => {
  const lastComponentRef = useRef({ component: null, props: {} });
  const [localSelection, setLocalSelection] = useLocalStorage(
    PLAN_SELECTION_KEY,
  );
  const [showModal, setShowModal] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const offer = useOffer();
  const { fresh, dry, mixed, ...plansApi } = useProductRecommendations({
    couponCode: offer.code,
    petId,
  });
  const petApi = usePet(petId);
  const petsApi = usePets();
  const { lineItemForPet, ...cartApi } = useCartLineItemForPet(petId);
  const productApi = useProduct(lineItemForPet?.sku, {
    couponCode: offer.code,
  });
  const router = useRouter();
  const reporter = useReporter();
  const { isLoading: isSessionLoading } = useSessionCustomer();

  const {
    path: [path, slug],
    ...params
  } = router.query;
  const pathname = slug ? `${path}/${slug}` : path;

  const [state, send, service] = usePlanSelectionMachine();

  const showRemovePetButton = petsApi.pets?.length > 1;
  const productTypes = { fresh, dry, mixed };
  const onDeletePet = () => {
    onClickDeletePet(petId);
  };
  const isLoading =
    petApi.isLoading ||
    plansApi.isValidating ||
    productApi.isLoading ||
    isSessionLoading;

  useEffect(() => {
    if (!isLoading && state.name === STATE.initial) {
      const cartProduct = getCartProduct(productApi.product, lineItemForPet);
      let recipes;
      let mealPrepType;
      if (cartProduct) {
        recipes = cartProduct.variants[0].recipes;
        mealPrepType = getMealPrepTypeFromRecipes(recipes);
      } else if (localSelection) {
        recipes = localSelection.recipes;
        mealPrepType = localSelection.mealPrepType;
      }
      send(ACTIONS.initialLoad({ pathname, recipes, mealPrepType }));
    }
  }, [
    isLoading,
    productApi,
    lineItemForPet,
    state,
    send,
    localSelection,
    pathname,
  ]);

  useEffect(() => {
    // this is forcing LoadingView to render and grant plansApi.isValidating time to process
    // which fixes the double-triple+ rendering/analytics tag firing for the default render
    // of this component (which contains FreshRecipesSelectionForm)
    // please note that this fix is ONLY for when going backwards (Back in browser or clicking Edit)
    // from CheckoutContainer
    if (skipInterstitial && !showPlanSelection) {
      setTimeout(() => {
        setShowPlanSelection(true);
      }, 10);
    }
  }, [showPlanSelection, skipInterstitial]);

  useEffect(() => {
    if (
      pathname !== state.name &&
      state.name !== STATE.initial &&
      state.name !== STATE.submitCart
    ) {
      router.push({
        pathname: `${rootPath}${state.name}`,
        query: { ...params },
      });
    }
  }, [params, pathname, rootPath, router, state.name]);

  useEffect(() => {
    if (state.context.mealPrepType) {
      setLocalSelection(state.context, { skipRender: true });
    }
  }, [state.context, setLocalSelection]);

  const cartProduct = getCartProduct(productApi.product, lineItemForPet);

  // These are data & hooks that must be passed down to the component
  let props = {
    planSelectionInfo,
    returnToQuiz,
    petId,
    noRecipesInfo,
    onClickDeletePet,
    finishPlanSelection,
    productTypes,
    selectedProductType: state.context.mealPrepType,
    selectedFreshRecipes: state.context.recipes.fresh,
    selectedDryRecipes: state.context.recipes.dry,
    cartProduct,
    cartApi,
    petApi,
    reporter,
    offer,
    setShowModal,
    setLocalSelection,
    onDeletePet,
    send,
    service,
  };

  let Component = components[state.name];

  if ((pathname !== state.name || !Component) && state.name !== STATE.initial) {
    Component = lastComponentRef.current.component;
    props = lastComponentRef.current.props;
  } else lastComponentRef.current = { component: Component, props };

  if ((isLoading && skipInterstitial) || state.name === STATE.initial) {
    return <LoadingView />;
  } else if (!showPlanSelection && !skipInterstitial) {
    return (
      <PrefixedReporterProvider prefix="Onboarding">
        <InterstitialContainer>
          <EmptyHeader />
          <LoadingInterstitial>
            <PlanLoader
              messages={interpolate(planLoaderMessages, {
                discount: offer.formattedAmount,
                pupName: petApi?.pet?.name || 'your pup',
              })}
              onClick={() => {
                setShowPlanSelection(true);
              }}
              buttonText={planLoaderButtonText}
              isLoading={isLoading}
            />
          </LoadingInterstitial>
        </InterstitialContainer>
      </PrefixedReporterProvider>
    );
  }

  if (isServerError(plansApi.error)) {
    return <LoadingView variant="failed" />;
  }

  if (showNoRecipeFound(productTypes)) {
    return (
      <Wrapper>
        <EmptyHeader />
        <NoRecipesFound
          {...interpolate(noRecipesInfo, {
            pupName: petApi.pet.name,
          })}
          allergies={petApi.pet.allergies.map(({ name }) => ({
            label: name,
          }))}
          onClick={() => {
            router.push(`/onboarding/quiz/allergies/?pet_id=${petId}`);
          }}
          onClickDeletePet={showRemovePetButton ? onDeletePet : null}
        />
        {body.map(content => (
          <CMSBlok key={content._uid} content={content} />
        ))}
      </Wrapper>
    );
  }
  return (
    <Wrapper>
      <Header step={STATE_TO_STEP_REFERENCE[state.name]} />
      <Component {...props} />
      {body.map(content => (
        <CMSBlok key={content._uid} content={content} />
      ))}
      <ServerSideErrorModal
        shown={showModal}
        headerText="Hmm, something’s not right."
        errorMessage="We weren’t able to complete your request, please refresh the page and try again."
        onCloseModal={() => setShowModal(false)}
      />
    </Wrapper>
  );
};

PlanSelectionContainer.displayName = 'PlanSelectionContainer';

PlanSelectionContainer.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  noRecipesInfo: PropTypes.object.isRequired,
  planLoaderMessages: PropTypes.arrayOf(PropTypes.object).isRequired,
  planLoaderButtonText: PropTypes.string.isRequired,
  petId: PropTypes.string.isRequired,
  body: PropTypes.arrayOf(
    PropTypes.shape({
      _uid: PropTypes.string.isRequired,
    }),
  ).isRequired,
  returnToQuiz: PropTypes.func.isRequired,
  onClickDeletePet: PropTypes.func.isRequired,
  skipInterstitial: PropTypes.bool,
  finishPlanSelection: PropTypes.func.isRequired,
  rootPath: PropTypes.string.isRequired,
};
